/**
 * ListeContratsPage.jsx
 *
 * Gestion des contrats : table complète, CRUD, détail tickets.
 * Self-contained : tout est ici sauf les composants partagés de ContratPageShared.
 */

import { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SecurityIcon from '@mui/icons-material/Security';
import {
  MaterialReactTable,
  MRT_ToggleDensePaddingButton,
  MRT_ToggleFiltersButton,
  MRT_ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton,
  useMaterialReactTable,
} from 'material-react-table';

import ContratFormDialog from '../ContratFormDialog';
import {
  ContractAlerts,
  ContractFiltersBar,
  contratApi,
  fmtDays,
  fmtHours,
  formatCurrency,
  formatDateCell,
  getConfiguredDailyRate,
  getConfiguredDays,
  getConfiguredHourlyRate,
  getRawContractType,
  useContratPageData,
} from './ContratPageShared';

const SERVICE_METRIC_KEYS = ['admin_hours', 'engineer_hours', 'admin_days', 'engineer_days', 'pilotage_days', 'travel'];

const normalizeServiceMetric = (value) => {
  const normalized = String(value || '').trim().toLowerCase();
  return SERVICE_METRIC_KEYS.includes(normalized) ? normalized : '';
};

const normalizeProductCode = (value = '') =>
  String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

const DEFAULT_SECTION = 'GENERAL';

const normalizeSectionLabel = (value = '') => {
  const cleaned = String(value || '')
    .replace(/\s*-\s*/g, ' - ')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned ? cleaned.toUpperCase() : DEFAULT_SECTION;
};

const SECTION_SORT_ORDER = [
  'INFOGERANCE',
  'SERVICES - INFOGERANCE',
  'VM - CODIAL',
  'HOSTING - SAUVEGARDE',
  'HOSTING - PRESTATION HEBERGEMENT',
  'GENERAL',
];

const getSectionSortRank = (section = '') => {
  const normalizedSection = normalizeSectionLabel(section || DEFAULT_SECTION);
  const index = SECTION_SORT_ORDER.indexOf(normalizedSection);
  return index >= 0 ? index : 999;
};

const getBillingCycleSortRank = (cycle = '') => {
  const normalizedCycle = String(cycle || '').trim().toLowerCase();
  if (normalizedCycle === 'mensuel') return 0;
  if (normalizedCycle === 'annuel') return 1;
  return 2;
};

const normalizeCategoryText = (value = '') =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

const isInfogeranceServiceCategory = (category = '', subCategory = '') => {
  const normalizedCategory = normalizeCategoryText(category);
  const normalizedSubCategory = normalizeCategoryText(subCategory);
  return (normalizedCategory === 'service' || normalizedCategory === 'services')
    && (normalizedSubCategory === 'infogerance' || normalizedSubCategory === 'infogerances');
};

const detectServiceMetricFromText = (value = '') => {
  const normalized = String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  if (normalized.includes('heure') && normalized.includes('admin')) return 'admin_hours';
  if (normalized.includes('heure') && (normalized.includes('inge') || normalized.includes('ingenieur'))) return 'engineer_hours';
  if (normalized.includes('jour') && normalized.includes('admin')) return 'admin_days';
  if (normalized.includes('jour') && (normalized.includes('inge') || normalized.includes('ingenieur'))) return 'engineer_days';
  if (normalized.includes('pilotage')) return 'pilotage_days';
  if (normalized.includes('deplacement')) return 'travel';
  return '';
};

const detectServiceMetricFromCode = (code = '') => {
  const normalized = normalizeProductCode(code);
  if (!normalized) return '';
  if (normalized.includes('TRAVEL') || normalized.includes('DEPLACEMENT')) return 'travel';
  if (normalized.includes('PILOTAGE') && normalized.includes('DAY')) return 'pilotage_days';
  if (normalized.includes('ADMIN') && normalized.includes('HOUR')) return 'admin_hours';
  if (normalized.includes('ENGINEER') && normalized.includes('HOUR')) return 'engineer_hours';
  if (normalized.includes('INGE') && normalized.includes('HOUR')) return 'engineer_hours';
  if (normalized.includes('ADMIN') && normalized.includes('DAY')) return 'admin_days';
  if (normalized.includes('ENGINEER') && normalized.includes('DAY')) return 'engineer_days';
  if (normalized.includes('INGE') && normalized.includes('DAY')) return 'engineer_days';
  return '';
};

const resolveServiceMetric = ({ serviceMetric, productCode, productName } = {}) => (
  normalizeServiceMetric(serviceMetric)
  || detectServiceMetricFromCode(productCode)
  || detectServiceMetricFromText(productName)
);

const getArticleLineOrder = (line = {}) => {
  const parsed = Number(line?.lineOrder);
  return Number.isFinite(parsed) ? parsed : null;
};

const getServiceLineSortRank = (row = {}) => {
  const metric = normalizeServiceMetric(row?.serviceMetric);
  if (metric === 'admin_hours') return 0;
  if (metric === 'engineer_hours') return 1;
  if (metric === 'admin_days') return 2;
  if (metric === 'engineer_days') return 3;
  if (metric === 'pilotage_days') return 4;
  if (metric === 'travel') return 7;

  const normalizedLabel = normalizeCategoryText(row?.label || '');
  if (normalizedLabel.includes('helpdesk')) return 5;
  if (normalizedLabel.includes('frais') && normalizedLabel.includes('prelev')) return 6;
  if (normalizedLabel.includes('deplacement')) return 7;
  return 50;
};

const getLegacyServiceRows = (contract) => {
  const adminHourlyRate = getConfiguredHourlyRate(contract, 'adminHourlyRate', 'adminDailyRate');
  const engineerHourlyRate = getConfiguredHourlyRate(contract, 'engineerHourlyRate', 'engineerDailyRate');
  const pilotageDailyRate = getConfiguredDailyRate(contract, 'pilotageDailyRate', 'pilotageHourlyRate');
  const adminDailyRate = getConfiguredDailyRate(contract, 'adminDailyRate', 'adminHourlyRate');
  const engineerDailyRate = getConfiguredDailyRate(contract, 'engineerDailyRate', 'engineerHourlyRate');

  return [
    { label: 'Heures admin', cycle: 'mensuel', quantity: Number(contract?.adminHours || 0), unitPrice: adminHourlyRate, serviceMetric: 'admin_hours', section: DEFAULT_SECTION },
    { label: 'Heures ingé', cycle: 'mensuel', quantity: Number(contract?.engineerHours || 0), unitPrice: engineerHourlyRate, serviceMetric: 'engineer_hours', section: DEFAULT_SECTION },
    { label: 'Jours admin', cycle: 'annuel', quantity: Number(contract?.adminDays || 0), unitPrice: adminDailyRate, serviceMetric: 'admin_days', section: DEFAULT_SECTION },
    { label: 'Jours ingé', cycle: 'annuel', quantity: Number(contract?.engineerDays || 0), unitPrice: engineerDailyRate, serviceMetric: 'engineer_days', section: DEFAULT_SECTION },
    { label: 'Jours pilotage', cycle: 'annuel', quantity: getConfiguredDays(contract, 'pilotageDays', 'pilotageHours'), unitPrice: pilotageDailyRate, serviceMetric: 'pilotage_days', section: DEFAULT_SECTION },
    { label: 'Déplacements', cycle: 'annuel', quantity: Number(contract?.travelCount || 0), unitPrice: Number(contract?.travelUnitPrice || 0), serviceMetric: 'travel', section: DEFAULT_SECTION },
  ];
};

const getServiceRows = (contract) => {
  const serviceLines = (contract?.articles || [])
    .map((line) => {
      const metric = resolveServiceMetric({
        serviceMetric: line?.serviceMetric || line?.article?.serviceMetric,
        productCode: line?.productCode || line?.article?.code,
        productName: line?.productName || line?.article?.name,
      });
      const isService = metric || isInfogeranceServiceCategory(
        line?.category || line?.article?.category || '',
        line?.subCategory || line?.article?.subCategory || ''
      );
      if (!isService) return null;
      return {
        label: line?.productName || line?.article?.name || 'Service',
        cycle: String(line?.billingCycle || line?.article?.billingCycle || 'oneshot').toLowerCase(),
        quantity: Number(line?.quantity || 0),
        unitPrice: Number(line?.unitPrice ?? line?.article?.unitPrice ?? 0),
        section: normalizeSectionLabel(line?.section || DEFAULT_SECTION),
        serviceMetric: metric || '',
      };
    })
    .filter(Boolean);

  if (serviceLines.length > 0) return serviceLines;
  return getLegacyServiceRows(contract);
};

const getProductRows = (contract) => (
  (contract?.articles || [])
    .map((line) => {
      const serviceMetric = resolveServiceMetric({
        serviceMetric: line?.serviceMetric || line?.article?.serviceMetric,
        productCode: line?.productCode || line?.article?.code,
        productName: line?.productName || line?.article?.name,
      });
      return {
        isService: Boolean(serviceMetric || isInfogeranceServiceCategory(
        line?.category || line?.article?.category || '',
        line?.subCategory || line?.article?.subCategory || ''
      )),
        lineOrder: getArticleLineOrder(line),
        serviceMetric: serviceMetric || '',
        section: normalizeSectionLabel(line?.section || DEFAULT_SECTION),
        label: line?.productName || line?.article?.name || 'Produit',
        cycle: String(line?.billingCycle || line?.article?.billingCycle || 'oneshot').toLowerCase(),
        quantity: Number(line?.quantity || 0),
        unitPrice: Number(line?.unitPrice ?? line?.article?.unitPrice ?? 0),
      };
    })
    .sort((a, b) => {
      if (a.lineOrder != null && b.lineOrder != null && a.lineOrder !== b.lineOrder) {
        return a.lineOrder - b.lineOrder;
      }
      if (a.lineOrder != null && b.lineOrder == null) return -1;
      if (a.lineOrder == null && b.lineOrder != null) return 1;

      const sectionRankDiff = getSectionSortRank(a.section) - getSectionSortRank(b.section);
      if (sectionRankDiff !== 0) return sectionRankDiff;

      const sectionDiff = String(a.section || '').localeCompare(String(b.section || ''), 'fr');
      if (sectionDiff !== 0) return sectionDiff;

      const serviceDiff = Number(b.isService) - Number(a.isService); // services first
      if (serviceDiff !== 0) return serviceDiff;

      if (a.isService && b.isService) {
        const serviceRankDiff = getServiceLineSortRank(a) - getServiceLineSortRank(b);
        if (serviceRankDiff !== 0) return serviceRankDiff;
      }

      const cycleDiff = getBillingCycleSortRank(a.cycle) - getBillingCycleSortRank(b.cycle);
      if (cycleDiff !== 0) return cycleDiff;

      return String(a.label || '').localeCompare(String(b.label || ''), 'fr');
    })
);

const getAnnualizedLineTotal = (row) => {
  const lineTotal = Number(row?.quantity || 0) * Number(row?.unitPrice || 0);
  if (row?.cycle === 'mensuel') return lineTotal * 12;
  if (row?.cycle === 'annuel') return lineTotal;
  return lineTotal;
};

const getMonthlyLineTotal = (row) => {
  const lineTotal = Number(row?.quantity || 0) * Number(row?.unitPrice || 0);
  if (row?.cycle === 'mensuel') return lineTotal;
  if (row?.cycle === 'annuel') return lineTotal / 12;
  return 0;
};

const groupRowsBySection = (rows = [], billingFrequency = 'annuel') => {
  const grouped = new Map();
  rows.forEach((row) => {
    const section = normalizeSectionLabel(row?.section || DEFAULT_SECTION);
    const current = grouped.get(section) || [];
    current.push(row);
    grouped.set(section, current);
  });

  const isMonthlyBilling = String(billingFrequency || '').toLowerCase() === 'mensuel';

  return Array.from(grouped.entries())
    .sort((a, b) => {
      const sectionRankDiff = getSectionSortRank(a[0]) - getSectionSortRank(b[0]);
      if (sectionRankDiff !== 0) return sectionRankDiff;
      return a[0].localeCompare(b[0], 'fr');
    })
    .map(([section, sectionRows]) => ({
      section,
      rows: sectionRows,
      subtotalMonthly: sectionRows.reduce((sum, row) => sum + getMonthlyLineTotal(row), 0),
      subtotalAnnual: sectionRows.reduce((sum, row) => sum + getAnnualizedLineTotal(row), 0),
      subtotalDisplay: isMonthlyBilling
        ? sectionRows.reduce((sum, row) => sum + getMonthlyLineTotal(row), 0)
        : sectionRows.reduce((sum, row) => sum + getAnnualizedLineTotal(row), 0),
      subtotalUnitLabel: isMonthlyBilling ? '€/mois' : '€/an',
    }));
};

const getServiceMetricQuantity = (contract, metric) =>
  getServiceRows(contract)
    .filter((row) => row.serviceMetric === metric)
    .reduce((sum, row) => sum + Number(row.quantity || 0), 0);

const getServiceMetricUnitPrice = (contract, metric) =>
  getServiceRows(contract)
    .filter((row) => row.serviceMetric === metric)
    .reduce((latest, row) => (Number(row.unitPrice || 0) > 0 ? Number(row.unitPrice || 0) : latest), 0);

const getContractEstimatedValue = (contract) => {
  const serviceRows = getServiceRows(contract);
  const monthlyValue =
    serviceRows
      .filter((row) => row.cycle === 'mensuel')
      .reduce((sum, row) => sum + (row.quantity * row.unitPrice), 0);

  const annualValue =
    serviceRows
      .filter((row) => row.cycle === 'annuel')
      .reduce((sum, row) => sum + (row.quantity * row.unitPrice), 0);

  return monthlyValue * 12 + annualValue;
};

const getEsetComparison = (contract = {}) => contract?.esetComparison || {
  contract: { quantity: 0 },
  technical: { quantity: 0, used: 0 },
  delta: { contractVsTechnical: 0, contractVsUsed: 0 },
};

const getO365Comparison = (contract = {}) => contract?.o365Comparison || {
  contract: { quantity: 0 },
  technical: { quantity: 0, used: 0 },
  delta: { contractVsTechnical: 0, contractVsUsed: 0 },
};

const getEsetProductKey = (row = {}) => {
  const key = String(row?.productKey || '').trim().toLowerCase();
  if (key) return key;
  return String(row?.productName || '').trim().toLowerCase();
};

const getO365ProductKey = (row = {}) => {
  const productCode = normalizeProductCode(row?.productCode || '');
  if (productCode) return productCode;
  const productKey = normalizeProductCode(row?.productKey || '');
  if (productKey) return productKey;
  return String(row?.productName || '').trim().toLowerCase();
};

const getEsetProductComparisonRows = (contract = {}) => {
  const comparison = getEsetComparison(contract);
  const contractRows = Array.isArray(comparison?.contract?.byProduct) ? comparison.contract.byProduct : [];
  const technicalRows = Array.isArray(comparison?.technical?.byProduct) ? comparison.technical.byProduct : [];
  const byProduct = new Map();

  contractRows.forEach((row) => {
    const key = getEsetProductKey(row);
    if (!key) return;
    const existing = byProduct.get(key) || {
      key,
      label: row?.productName || row?.productKey || 'ESET',
      contractQuantity: 0,
      technicalUsed: 0,
      technicalQuantity: 0,
    };
    existing.contractQuantity += Number(row?.quantity || 0);
    if (!existing.label && (row?.productName || row?.productKey)) {
      existing.label = row.productName || row.productKey;
    }
    byProduct.set(key, existing);
  });

  technicalRows.forEach((row) => {
    const key = getEsetProductKey(row);
    if (!key) return;
    const existing = byProduct.get(key) || {
      key,
      label: row?.productName || row?.productKey || 'ESET',
      contractQuantity: 0,
      technicalUsed: 0,
      technicalQuantity: 0,
    };
    existing.technicalUsed += Number(row?.used || 0);
    existing.technicalQuantity += Number(row?.quantity || 0);
    if (!existing.label && (row?.productName || row?.productKey)) {
      existing.label = row.productName || row.productKey;
    }
    byProduct.set(key, existing);
  });

  return Array.from(byProduct.values())
    .map((row) => ({
      ...row,
      deltaVsUsed: row.contractQuantity - row.technicalUsed,
      deltaVsTechnical: row.contractQuantity - row.technicalQuantity,
    }))
    .sort((a, b) => String(a.label || '').localeCompare(String(b.label || ''), 'fr'));
};

const getO365ProductComparisonRows = (contract = {}) => {
  const comparison = getO365Comparison(contract);
  const contractRows = Array.isArray(comparison?.contract?.byProduct) ? comparison.contract.byProduct : [];
  const technicalRows = Array.isArray(comparison?.technical?.byProduct) ? comparison.technical.byProduct : [];
  const byProduct = new Map();

  contractRows.forEach((row) => {
    const key = getO365ProductKey(row);
    if (!key) return;
    const existing = byProduct.get(key) || {
      key,
      label: row?.productName || row?.productCode || row?.productKey || 'O365',
      contractQuantity: 0,
      technicalUsed: 0,
      technicalQuantity: 0,
    };
    existing.contractQuantity += Number(row?.quantity || 0);
    if (!existing.label && (row?.productName || row?.productCode || row?.productKey)) {
      existing.label = row.productName || row.productCode || row.productKey;
    }
    byProduct.set(key, existing);
  });

  technicalRows.forEach((row) => {
    const key = getO365ProductKey(row);
    if (!key) return;
    const existing = byProduct.get(key) || {
      key,
      label: row?.productName || row?.productCode || row?.productKey || 'O365',
      contractQuantity: 0,
      technicalUsed: 0,
      technicalQuantity: 0,
    };
    existing.technicalUsed += Number(row?.used || 0);
    existing.technicalQuantity += Number(row?.quantity || 0);
    if (!existing.label && (row?.productName || row?.productCode || row?.productKey)) {
      existing.label = row.productName || row.productCode || row.productKey;
    }
    byProduct.set(key, existing);
  });

  return Array.from(byProduct.values())
    .map((row) => ({
      ...row,
      deltaVsUsed: row.contractQuantity - row.technicalUsed,
      deltaVsTechnical: row.contractQuantity - row.technicalQuantity,
    }))
    .sort((a, b) => String(a.label || '').localeCompare(String(b.label || ''), 'fr'));
};

const getEsetStatus = (contract = {}) => {
  const comparison = getEsetComparison(contract);
  const contractQty = Number(comparison?.contract?.quantity || 0);
  const technicalUsed = Number(comparison?.technical?.used || 0);
  const technicalTotal = Number(comparison?.technical?.quantity || 0);
  const hasTechnicalData = technicalTotal > 0 || technicalUsed > 0;
  const delta = contractQty - technicalTotal;

  if (!hasTechnicalData) {
    return {
      hidden: true,
      color: 'default',
      label: 'Aucune donnée',
      tooltip: 'Aucune donnée technique ESET pour ce client.',
      chipSx: {},
      isOverrun: false,
    };
  }

  if (delta === 0) {
    return {
      hidden: false,
      color: 'success',
      label: 'OK',
      tooltip: `ESET équilibré: contrat ${contractQty.toFixed(2)} = technique ${technicalTotal.toFixed(2)}`,
      chipSx: {},
      isOverrun: false,
    };
  }

  if (delta < 0) {
    return {
      hidden: false,
      color: 'error',
      label: 'Dépassement',
      tooltip: `ESET en dépassement: contrat ${contractQty.toFixed(2)} < technique ${technicalTotal.toFixed(2)} (${delta.toFixed(2)})`,
      chipSx: {},
      isOverrun: true,
    };
  }

  return {
    hidden: false,
    color: 'warning',
    label: 'Sous consommation',
    tooltip: `ESET sous consommé: contrat ${contractQty.toFixed(2)} > technique ${technicalTotal.toFixed(2)} (+${delta.toFixed(2)})`,
    chipSx: {
      color: '#b54708',
      borderColor: '#f79009',
      backgroundColor: '#fffaeb',
    },
    isOverrun: false,
  };
};

const getO365Status = (contract = {}) => {
  const comparison = getO365Comparison(contract);
  const contractQty = Number(comparison?.contract?.quantity || 0);
  const technicalUsed = Number(comparison?.technical?.used || 0);
  const technicalTotal = Number(comparison?.technical?.quantity || 0);
  const hasTechnicalData = technicalTotal > 0 || technicalUsed > 0;
  const delta = contractQty - technicalTotal;

  if (!hasTechnicalData) {
    return {
      hidden: true,
      color: 'default',
      label: 'Aucune donnée',
      tooltip: 'Aucune donnée technique O365 pour ce client.',
      chipSx: {},
      isOverrun: false,
    };
  }

  if (delta === 0) {
    return {
      hidden: false,
      color: 'success',
      label: 'OK',
      tooltip: `O365 équilibré: contrat ${contractQty.toFixed(2)} = technique ${technicalTotal.toFixed(2)}`,
      chipSx: {},
      isOverrun: false,
    };
  }

  if (delta < 0) {
    return {
      hidden: false,
      color: 'error',
      label: 'Dépassement',
      tooltip: `O365 en dépassement: contrat ${contractQty.toFixed(2)} < technique ${technicalTotal.toFixed(2)} (${delta.toFixed(2)})`,
      chipSx: {},
      isOverrun: true,
    };
  }

  return {
    hidden: false,
    color: 'warning',
    label: 'Sous consommation',
    tooltip: `O365 sous consommé: contrat ${contractQty.toFixed(2)} > technique ${technicalTotal.toFixed(2)} (+${delta.toFixed(2)})`,
    chipSx: {
      color: '#b54708',
      borderColor: '#f79009',
      backgroundColor: '#fffaeb',
    },
    isOverrun: false,
  };
};

const getContractMonthlyValue = (contract) => {
  const serviceRows = getServiceRows(contract);

  const monthlyValue =
    serviceRows
      .filter((row) => row.cycle === 'mensuel')
      .reduce((sum, row) => sum + (row.quantity * row.unitPrice), 0);

  const annualValue =
    serviceRows
      .filter((row) => row.cycle === 'annuel')
      .reduce((sum, row) => sum + (row.quantity * row.unitPrice), 0);

  return monthlyValue + (annualValue / 12);
};

const getContractProductsAnnualValue = (contract) => (
  (contract?.articles || []).reduce((sum, line) => {
    const metric = resolveServiceMetric({
      serviceMetric: line?.serviceMetric || line?.article?.serviceMetric,
      productCode: line?.productCode || line?.article?.code,
      productName: line?.productName || line?.article?.name,
    });
    const isService = metric || isInfogeranceServiceCategory(
      line?.category || line?.article?.category || '',
      line?.subCategory || line?.article?.subCategory || ''
    );
    if (isService) return sum;
    const quantity = Number(line?.quantity || 0);
    const unitPrice = Number(line?.unitPrice ?? line?.article?.unitPrice ?? 0);
    const lineTotal = quantity * unitPrice;
    const billingCycle = String(line?.billingCycle || line?.article?.billingCycle || 'oneshot').toLowerCase();

    if (billingCycle === 'mensuel') return sum + (lineTotal * 12);
    if (billingCycle === 'annuel') return sum + lineTotal;
    return sum + lineTotal;
  }, 0)
);

const getContractProductsMonthlyValue = (contract) => (
  (contract?.articles || []).reduce((sum, line) => {
    const metric = resolveServiceMetric({
      serviceMetric: line?.serviceMetric || line?.article?.serviceMetric,
      productCode: line?.productCode || line?.article?.code,
      productName: line?.productName || line?.article?.name,
    });
    const isService = metric || isInfogeranceServiceCategory(
      line?.category || line?.article?.category || '',
      line?.subCategory || line?.article?.subCategory || ''
    );
    if (isService) return sum;
    const quantity = Number(line?.quantity || 0);
    const unitPrice = Number(line?.unitPrice ?? line?.article?.unitPrice ?? 0);
    const lineTotal = quantity * unitPrice;
    const billingCycle = String(line?.billingCycle || line?.article?.billingCycle || 'oneshot').toLowerCase();

    if (billingCycle === 'mensuel') return sum + lineTotal;
    if (billingCycle === 'annuel') return sum + (lineTotal / 12);
    return sum;
  }, 0)
);

const getContractTotalAnnualValue = (contract) =>
  getContractEstimatedValue(contract) + getContractProductsAnnualValue(contract);

const getContractTotalMonthlyValue = (contract) =>
  getContractMonthlyValue(contract) + getContractProductsMonthlyValue(contract);

const getContractTypeLabel = (contract) => {
  const type = getRawContractType(contract);
  if (type === 'infogerance') return 'Infogérance';
  if (type === 'cadre') return 'Cadre';
  if (type === 'basique') return 'Basique';
  return 'Non défini';
};

const getContractTypeColor = (contract) => {
  const type = getRawContractType(contract);
  if (type === 'infogerance') return 'success';
  if (type === 'cadre') return 'info';
  if (type === 'basique') return 'default';
  return 'warning';
};

const getPaymentTypeLabel = (contract) => {
  const raw = String(contract?.paymentType || '').trim().toLowerCase();
  if (raw === 'virement') return 'Virement';
  if (raw === 'prelevement') return 'Prélèvement';
  return 'Non défini';
};

const getPaymentTypeShortLabel = (contract) => {
  const raw = String(contract?.paymentType || '').trim().toLowerCase();
  if (raw === 'virement') return 'VIR';
  if (raw === 'prelevement') return 'PRE';
  return '—';
};

const getPaymentTypeColor = (contract) => {
  const raw = String(contract?.paymentType || '').trim().toLowerCase();
  if (raw === 'virement') return 'info';
  if (raw === 'prelevement') return 'success';
  return 'default';
};

const normalizeMatrixLine = (line = {}) => ({
  sourceMatrixItemId: line._id || line.sourceMatrixItemId || null,
  category: line.category || '',
  competence: line.competence || line.easyRedmineSkill || '',
  detail: line.detail || '',
  level: line.level || line.profile || '',
  profile: line.profile || 'admin',
  easyRedmineSkill: line.easyRedmineSkill || line.competence || '',
  easyRedmineLevel: line.easyRedmineLevel || '',
  matrixLevel: line.matrixLevel || '',
  active: line.active !== false,
  included: line.included !== false,
});

const normalizeMatrixText = (value) => String(value || '').trim().toLowerCase();

const buildMatrixMatchKey = (line = {}) => [
  normalizeMatrixText(line.category),
  normalizeMatrixText(line.easyRedmineSkill || line.competence),
  normalizeMatrixText(line.easyRedmineLevel),
  normalizeMatrixText(line.profile || line.level),
].join('||');

const buildEditableContractMatrix = (contract = {}) => {
  const current = (contract?.competences || []).map(normalizeMatrixLine);
  const base = (contract?.baseMatrixId?.competences || []).map(normalizeMatrixLine);
  if (base.length === 0) return current;

  const currentBySourceId = new Map(
    current
      .filter((item) => item?.sourceMatrixItemId)
      .map((item) => [String(item.sourceMatrixItemId), item])
  );
  const currentByCompositeKey = new Map(current.map((item) => [buildMatrixMatchKey(item), item]));

  const mergedFromBase = base.map((line) => {
    const bySourceId = line.sourceMatrixItemId
      ? currentBySourceId.get(String(line.sourceMatrixItemId))
      : null;
    const byComposite = currentByCompositeKey.get(buildMatrixMatchKey(line));
    const existing = bySourceId || byComposite || null;
    if (!existing) return line;
    return {
      ...line,
      sourceMatrixItemId: line.sourceMatrixItemId || existing.sourceMatrixItemId || null,
      included: existing.included !== false,
      active: existing.active !== false,
    };
  });

  const mergedSourceIds = new Set(
    mergedFromBase
      .map((item) => item?.sourceMatrixItemId)
      .filter(Boolean)
      .map((value) => String(value))
  );

  const orphanLines = current.filter((item) => {
    const normalized = normalizeMatrixLine(item);
    if (mergedSourceIds.has(String(normalized.sourceMatrixItemId))) return false;
    const existsInBase = mergedFromBase.some(
      (mergedItem) => buildMatrixMatchKey(mergedItem) === buildMatrixMatchKey(normalized)
    );
    if (existsInBase) return false;
    if (!item?.sourceMatrixItemId) return true;
    return !mergedSourceIds.has(String(item.sourceMatrixItemId));
  });

  return [...mergedFromBase, ...orphanLines.map(normalizeMatrixLine)];
};

const getMatrixCrossGroups = (contract, overrideLines = null) => {
  const lines = Array.isArray(overrideLines)
    ? overrideLines
    : (
      Array.isArray(contract?.competences) && contract.competences.length > 0
        ? contract.competences
        : contract?.baseMatrixId?.competences || []
    );

  const profileOrder = ['admin', 'ingenieur', 'pilotage'];
  const bySkill = new Map();

  lines.forEach((line, index) => {
    const skill = line?.easyRedmineSkill || line?.competence || 'Sans compétence 1';
    const level = line?.easyRedmineLevel || line?.level || 'Sans niveau';
    const profile = String(line?.profile || 'admin').toLowerCase();
    if (!profileOrder.includes(profile)) return;

    if (!bySkill.has(skill)) bySkill.set(skill, new Map());
    const byLevel = bySkill.get(skill);
    if (!byLevel.has(level)) {
      byLevel.set(level, {
        admin: null,
        ingenieur: null,
        pilotage: null,
      });
    }

    byLevel.get(level)[profile] = {
      included: line?.included !== false,
      active: line?.active !== false,
      _index: Number(line?._index ?? index),
    };
  });

  const groups = Array.from(bySkill.entries())
    .sort((a, b) => a[0].localeCompare(b[0], 'fr'))
    .map(([skill, levelMap]) => {
      const rows = Array.from(levelMap.entries())
        .sort((a, b) => a[0].localeCompare(b[0], 'fr'))
        .map(([level, cells]) => ({ level, cells }));
      return [skill, rows];
    });

  return groups;
};

const getMatrixFlatRows = (contract, overrideLines = null) =>
  getMatrixCrossGroups(contract, overrideLines).flatMap(([skill, rows]) =>
    rows.map((row) => ({
      skill,
      level: row.level,
      cells: row.cells,
    }))
  );

function ContractFinancialSummary({ contract }) {
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
      <Box>
        <Typography variant="caption" color="text.secondary">Heures admin</Typography>
        <Typography variant="body2">{fmtHours(getServiceMetricQuantity(contract, 'admin_hours'))}</Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Heures ingénieur</Typography>
        <Typography variant="body2">{fmtHours(getServiceMetricQuantity(contract, 'engineer_hours'))}</Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Jours pilotage</Typography>
        <Typography variant="body2">{fmtDays(getServiceMetricQuantity(contract, 'pilotage_days'))}</Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Jours admin</Typography>
        <Typography variant="body2">{fmtDays(getServiceMetricQuantity(contract, 'admin_days'))}</Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Jours ingénieur</Typography>
        <Typography variant="body2">{fmtDays(getServiceMetricQuantity(contract, 'engineer_days'))}</Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Valeur services annuelle</Typography>
        <Typography variant="body2" fontWeight={600}>{formatCurrency(getContractEstimatedValue(contract))}</Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Valeur services mensuelle</Typography>
        <Typography variant="body2" fontWeight={600}>{formatCurrency(getContractMonthlyValue(contract))}</Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Valeur produits annuelle</Typography>
        <Typography variant="body2" fontWeight={600}>{formatCurrency(getContractProductsAnnualValue(contract))}</Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Valeur produits mensuelle</Typography>
        <Typography variant="body2" fontWeight={600}>{formatCurrency(getContractProductsMonthlyValue(contract))}</Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Valeur totale annuelle</Typography>
        <Typography variant="body2" fontWeight={700}>{formatCurrency(getContractTotalAnnualValue(contract))}</Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Valeur totale mensuelle</Typography>
        <Typography variant="body2" fontWeight={700}>{formatCurrency(getContractTotalMonthlyValue(contract))}</Typography>
      </Box>
      <Box>
        <Typography variant="caption" color="text.secondary">Déplacements</Typography>
        <Typography variant="body2">
          {getServiceMetricQuantity(contract, 'travel')} x {formatCurrency(getServiceMetricUnitPrice(contract, 'travel'))}
        </Typography>
      </Box>
    </Box>
  );
}

export default function ContratListePage() {
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewContractOpen, setViewContractOpen] = useState(false);
  const [viewMatrixOpen, setViewMatrixOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [displayMode, setDisplayMode] = useState('table');
  const [matrixViewMode, setMatrixViewMode] = useState('compact');
  const [matrixEditMode, setMatrixEditMode] = useState(false);
  const [matrixDraft, setMatrixDraft] = useState([]);
  const [matrixSaving, setMatrixSaving] = useState(false);
  const [matrixError, setMatrixError] = useState('');

  const {
    articles,
    availableYears,
    clientAutocompleteOptions,
    competenceCatalog,
    customEndDate,
    customStartDate,
    dateMode,
    error,
    filteredContracts,
    loadArticles,
    loadContracts,
    loadMatrices,
    loading,
    matrices,
    mismatchContracts,
    organizations,
    rootOrganizationOptions,
    selectedInfogeranceFilter,
    selectedOrganizationId,
    selectedRootOrganizationId,
    selectedYear,
    setCustomEndDate,
    setCustomStartDate,
    setDateMode,
    setSelectedInfogeranceFilter,
    setSelectedOrganizationId,
    setSelectedRootOrganizationId,
    setSelectedYear,
    supportEntriesLoading,
    supportProjects,
  } = useContratPageData({
    includeMatrices: true,
    includeArticles: true,
    includeSupportProjects: true,
    includeEsetComparison: true,
    includeO365Comparison: true,
  });

  const openViewContractDialog = (contract) => {
    setViewing(contract);
    setViewContractOpen(true);
  };

  const openViewMatrixDialog = (contract) => {
    setViewing(contract);
    setMatrixDraft(buildEditableContractMatrix(contract));
    setMatrixEditMode(false);
    setMatrixError('');
    setViewMatrixOpen(true);
  };

  const toggleMatrixIncluded = (lineIndex) => {
    setMatrixDraft((current) => current.map((line, index) => (
      index === lineIndex ? { ...line, included: line?.included === false } : line
    )));
  };

  const saveMatrixChanges = async () => {
    if (!viewing?._id) return;
    setMatrixSaving(true);
    setMatrixError('');
    try {
      const competences = (matrixDraft || []).map((item) => ({
        sourceMatrixItemId: item.sourceMatrixItemId || undefined,
        category: item.category || '',
        competence: item.competence || item.easyRedmineSkill || '',
        detail: item.detail || '',
        level: item.level || item.profile || '',
        profile: item.profile || 'admin',
        easyRedmineSkill: item.easyRedmineSkill || '',
        easyRedmineLevel: item.easyRedmineLevel || '',
        matrixLevel: item.matrixLevel || '',
        active: item.active !== false,
        included: item.included !== false,
      }));
      await contratApi.put(`/api/contrat/${viewing._id}`, { competences });
      setViewing((current) => (current ? { ...current, competences } : current));
      setMatrixEditMode(false);
      loadContracts();
    } catch (e) {
      setMatrixError(
        e?.response?.data?.error || e?.response?.data?.message || e.message || 'Erreur',
      );
    } finally {
      setMatrixSaving(false);
    }
  };

  const deleteContract = async (contract) => {
    if (!window.confirm(`Supprimer le contrat "${contract.name}" ?`)) return;
    try {
      setDeleteError('');
      await contratApi.delete(`/api/contrat/${contract._id}`);
      loadContracts();
    } catch (e) {
      setDeleteError(
        e?.response?.data?.error || e?.response?.data?.message || e.message || 'Erreur',
      );
    }
  };

  // ── Colonnes ──────────────────────────────────────────────────────────────

  const columns = useMemo(
    () => [
      { accessorKey: 'name', header: 'Contrat' },
      { accessorKey: 'reference', header: 'Référence' },
      {
        accessorKey: 'Organization.name',
        header: 'Organisation',
        Cell: ({ row }) => row.original.Organization?.name || '—',
      },
      {
        id: 'type',
        header: 'Type',
        size: 120,
        Cell: ({ row }) => {
          const type = getRawContractType(row.original);
          if (type === 'infogerance') {
            return <Chip size="small" color="success" label="Infogérance" />;
          }
          if (type === 'cadre') {
            return <Chip size="small" color="info" label="Cadre" />;
          }
          if (type === 'basique') {
            return <Chip size="small" color="default" label="Basique" />;
          }
          return <Chip size="small" color="warning" label="Non défini" />;
        },
      },
      {
        accessorKey: 'startDate',
        header: 'Date début',
        Cell: ({ row }) => formatDateCell(row.original.startDate),
      },
      {
        id: 'paymentType',
        header: 'PAY',
        size: 68,
        minSize: 60,
        maxSize: 72,
        Cell: ({ row }) => (
          <Chip
            size="small"
            color={getPaymentTypeColor(row.original)}
            label={getPaymentTypeShortLabel(row.original)}
          />
        ),
      },
      {
        accessorKey: 'endDate',
        header: 'Date fin',
        Cell: ({ row }) => formatDateCell(row.original.endDate),
      },
      {
        id: 'status',
        header: 'Status',
        enableSorting: false,
        Cell: ({ row }) => {
          const esetStatus = getEsetStatus(row.original);
          const o365Status = getO365Status(row.original);
          if (esetStatus.hidden && o365Status.hidden) return null;
          return (
            <Stack direction="row" spacing={0.5}>
              {!esetStatus.hidden ? (
                <Tooltip title={esetStatus.tooltip}>
                  <Chip
                    size="small"
                    icon={<SecurityIcon />}
                    color={esetStatus.color}
                    label="AV"
                    variant="outlined"
                    sx={esetStatus.chipSx}
                  />
                </Tooltip>
              ) : null}
              {!o365Status.hidden ? (
                <Tooltip title={o365Status.tooltip}>
                  <Chip
                    size="small"
                    color={o365Status.color}
                    label="O365"
                    variant="outlined"
                    sx={o365Status.chipSx}
                  />
                </Tooltip>
              ) : null}
            </Stack>
          );
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        Cell: ({ row }) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Voir contrat">
              <IconButton
                size="small"
                onClick={() => openViewContractDialog(row.original)}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Voir matrice">
              <IconButton
                size="small"
                onClick={() => openViewMatrixDialog(row.original)}
              >
                <AccountTreeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Modifier">
              <IconButton
                size="small"
                onClick={() => {
                  setEditing(row.original);
                  setFormOpen(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Supprimer">
              <IconButton
                size="small"
                color="error"
                onClick={() => deleteContract(row.original)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [loadContracts],
  );

  const table = useMaterialReactTable({
    columns,
    data: filteredContracts,
    state: { isLoading: loading },
    initialState: { density: 'compact' },
    enablePagination: false,
    enableExpanding: true,
    renderToolbarInternalActions: ({ table: t }) => (
      <Box sx={{ display: 'flex', gap: 1 }}>
        <MRT_ToggleDensePaddingButton table={t} />
        <MRT_ToggleFiltersButton table={t} />
        <MRT_ToggleGlobalFilterButton table={t} />
        <MRT_ToggleFullScreenButton table={t} />
      </Box>
    ),
    renderDetailPanel: ({ row }) => (
      <Box sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={2}>
            <Typography variant="caption" color="text.secondary">
              Début: {formatDateCell(row.original.startDate)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Fin: {formatDateCell(row.original.endDate)}
            </Typography>
          </Stack>
          <ContractFinancialSummary contract={row.original} />
        </Stack>
      </Box>
    ),
  });

  const matrixLinesWithIndex = useMemo(
    () => (matrixDraft || []).map((line, index) => ({ ...line, _index: index })),
    [matrixDraft]
  );
  const matrixCrossGroups = useMemo(
    () => getMatrixCrossGroups(viewing, matrixLinesWithIndex),
    [matrixLinesWithIndex, viewing]
  );
  const matrixFlatRows = useMemo(
    () => getMatrixFlatRows(viewing, matrixLinesWithIndex),
    [matrixLinesWithIndex, viewing]
  );

  return (
    <Box>
      {/* ── En-tête ── */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Liste des contrats</Typography>
          <Typography variant="caption" color="text.secondary">
            {filteredContracts.length} contrat(s)
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setEditing(null); setFormOpen(true); }}
        >
          Nouveau contrat
        </Button>
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button
          variant={displayMode === 'table' ? 'contained' : 'outlined'}
          startIcon={<ViewListIcon />}
          onClick={() => setDisplayMode('table')}
        >
          Vue table
        </Button>
        <Button
          variant={displayMode === 'card' ? 'contained' : 'outlined'}
          startIcon={<ViewModuleIcon />}
          onClick={() => setDisplayMode('card')}
        >
          Vue card
        </Button>
      </Stack>

      {/* ── Filtres ── */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <ContractFiltersBar
          availableYears={availableYears}
          clientAutocompleteOptions={clientAutocompleteOptions}
          customEndDate={customEndDate}
          customStartDate={customStartDate}
          dateMode={dateMode}
          onChangeCustomEndDate={setCustomEndDate}
          onChangeCustomStartDate={setCustomStartDate}
          onChangeDateMode={setDateMode}
          onChangeInfogerance={setSelectedInfogeranceFilter}
          onChangeOrganization={setSelectedOrganizationId}
          onChangeRootOrganization={setSelectedRootOrganizationId}
          onChangeYear={setSelectedYear}
          rootOrganizationOptions={rootOrganizationOptions}
          selectedInfogeranceFilter={selectedInfogeranceFilter}
          selectedOrganizationId={selectedOrganizationId}
          selectedRootOrganizationId={selectedRootOrganizationId}
          selectedYear={selectedYear}
        />
      </Paper>

      {/* ── Alertes & erreurs ── */}
      <ContractAlerts
        competenceCatalog={competenceCatalog}
        mismatchContracts={mismatchContracts}
        supportEntriesLoading={supportEntriesLoading}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}

      {/* ── Vue ── */}
      {displayMode === 'table' ? (
        <MaterialReactTable table={table} />
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))',
            gap: 2,
          }}
        >
          {filteredContracts.map((contract) => {
            return (
              <Paper key={contract._id} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={700}>{contract.name || 'Sans nom'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {contract.reference || 'Sans référence'} • {contract.Organization?.name || 'Sans organisation'}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      color={getContractTypeColor(contract)}
                      label={getContractTypeLabel(contract)}
                    />
                  </Stack>

                  <Stack direction="row" spacing={2}>
                    <Typography variant="caption" color="text.secondary">
                      Début: {formatDateCell(contract.startDate)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Fin: {formatDateCell(contract.endDate)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Paiement: {getPaymentTypeLabel(contract)}
                    </Typography>
                  </Stack>

                  <Typography variant="body2">
                    Total annuel: <strong>{formatCurrency(getContractTotalAnnualValue(contract))}</strong>
                  </Typography>
                  <Typography variant="body2">
                    Total mensuel: <strong>{formatCurrency(getContractTotalMonthlyValue(contract))}</strong>
                  </Typography>
                  <Stack direction="row" spacing={0.5}>
                    {(() => {
                      const esetStatus = getEsetStatus(contract);
                      const o365Status = getO365Status(contract);
                      if (esetStatus.hidden && o365Status.hidden) return null;
                      return (
                        <>
                          {!esetStatus.hidden ? (
                            <Tooltip title={esetStatus.tooltip}>
                              <Chip
                                size="small"
                                icon={<SecurityIcon />}
                                color={esetStatus.color}
                                label="AV"
                                variant="outlined"
                                sx={esetStatus.chipSx}
                              />
                            </Tooltip>
                          ) : null}
                          {!o365Status.hidden ? (
                            <Tooltip title={o365Status.tooltip}>
                              <Chip
                                size="small"
                                color={o365Status.color}
                                label="O365"
                                variant="outlined"
                                sx={o365Status.chipSx}
                              />
                            </Tooltip>
                          ) : null}
                        </>
                      );
                    })()}
                  </Stack>

                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title="Voir contrat">
                      <IconButton size="small" onClick={() => openViewContractDialog(contract)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Voir matrice">
                      <IconButton size="small" onClick={() => openViewMatrixDialog(contract)}>
                        <AccountTreeIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Modifier">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditing(contract);
                          setFormOpen(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Supprimer">
                      <IconButton size="small" color="error" onClick={() => deleteContract(contract)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* ── Dialogs ── */}
      <ContratFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={() => {
          setFormOpen(false);
          loadContracts();
          loadMatrices();
          loadArticles();
        }}
        editing={editing}
        organizations={organizations}
        matrices={matrices}
        articles={articles}
        supportProjects={supportProjects}
        api={contratApi}
      />

      <Dialog open={viewContractOpen} onClose={() => setViewContractOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>
          {`Contrat - ${viewing?.name || 'Sans nom'}`}
        </DialogTitle>
        <DialogContent dividers>
          {viewing ? (
            <Stack spacing={2}>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Comparaison ESET (licences)</Typography>
                {(() => {
                  const esetComparison = getEsetComparison(viewing);
                  const esetRows = getEsetProductComparisonRows(viewing);
                  const debug = esetComparison?.debug || {};

                  return (
                    <Stack spacing={1}>
                      <Typography variant="caption" color="text.secondary">
                        Join: {debug.joinMode || 'none'} | Lignes contrat ESET matchées: {Number(debug.matchedContractLines || 0)}
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Produit ESET</TableCell>
                              <TableCell align="right">Contrat</TableCell>
                              <TableCell align="right">Tech utilisée</TableCell>
                              <TableCell align="right">Tech totale</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {esetRows.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4}>Aucune ligne ESET trouvée pour ce contrat.</TableCell>
                              </TableRow>
                            ) : (
                              esetRows.map((row) => (
                                <TableRow key={`eset-row-${row.key}`}>
                                  <TableCell>{row.label || 'ESET'}</TableCell>
                                  <TableCell align="right">{Number(row.contractQuantity || 0).toFixed(2)}</TableCell>
                                  <TableCell align="right">{Number(row.technicalUsed || 0).toFixed(2)}</TableCell>
                                  <TableCell align="right">{Number(row.technicalQuantity || 0).toFixed(2)}</TableCell>
                                </TableRow>
                              ))
                            )}
                            <TableRow sx={{ backgroundColor: '#f7fafc' }}>
                              <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {Number(esetComparison?.contract?.quantity || 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {Number(esetComparison?.technical?.used || 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {Number(esetComparison?.technical?.quantity || 0).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Stack>
                  );
                })()}
              </Paper>

              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Comparaison O365 (licences)</Typography>
                {(() => {
                  const o365Comparison = getO365Comparison(viewing);
                  const o365Rows = getO365ProductComparisonRows(viewing);
                  const debug = o365Comparison?.debug || {};

                  return (
                    <Stack spacing={1}>
                      <Typography variant="caption" color="text.secondary">
                        Join: {debug.joinMode || 'none'} | Lignes contrat O365 matchées: {Number(debug.matchedContractLines || 0)}
                      </Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Produit O365</TableCell>
                              <TableCell align="right">Contrat</TableCell>
                              <TableCell align="right">Tech utilisée</TableCell>
                              <TableCell align="right">Tech totale</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {o365Rows.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4}>Aucune ligne O365 trouvée pour ce contrat.</TableCell>
                              </TableRow>
                            ) : (
                              o365Rows.map((row) => (
                                <TableRow key={`o365-row-${row.key}`}>
                                  <TableCell>{row.label || 'O365'}</TableCell>
                                  <TableCell align="right">{Number(row.contractQuantity || 0).toFixed(2)}</TableCell>
                                  <TableCell align="right">{Number(row.technicalUsed || 0).toFixed(2)}</TableCell>
                                  <TableCell align="right">{Number(row.technicalQuantity || 0).toFixed(2)}</TableCell>
                                </TableRow>
                              ))
                            )}
                            <TableRow sx={{ backgroundColor: '#f7fafc' }}>
                              <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {Number(o365Comparison?.contract?.quantity || 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {Number(o365Comparison?.technical?.used || 0).toFixed(2)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {Number(o365Comparison?.technical?.quantity || 0).toFixed(2)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Stack>
                  );
                })()}
              </Paper>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(5, 1fr)' },
                  gap: 1.5,
                }}
              >
                <Box>
                  <Typography variant="caption" color="text.secondary">Référence</Typography>
                  <Typography variant="body2">{viewing.reference || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Organisation</Typography>
                  <Typography variant="body2">{viewing.Organization?.name || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Type</Typography>
                  <Typography variant="body2">{getContractTypeLabel(viewing)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Facturation</Typography>
                  <Typography variant="body2">{viewing.billingFrequency === 'annuel' ? 'Annuel' : 'Mensuel'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Paiement</Typography>
                  <Typography variant="body2">{getPaymentTypeLabel(viewing)}</Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', lg: '2fr 1fr' },
                  gap: 2,
                }}
              >
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Articles</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Produit</TableCell>
                          <TableCell align="center">Type</TableCell>
                          <TableCell align="center">Périodicité</TableCell>
                          <TableCell align="right">Qté</TableCell>
                          <TableCell align="right">Prix unitaire</TableCell>
                          <TableCell align="right">Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {getProductRows(viewing).length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={6}>Aucun article</TableCell>
                          </TableRow>
                        ) : (
                          groupRowsBySection(getProductRows(viewing), viewing?.billingFrequency).flatMap((group) => [
                            (
                              <TableRow key={`product-group-${group.section}`} sx={{ backgroundColor: '#cdebf7' }}>
                                <TableCell colSpan={6} sx={{ fontWeight: 700 }}>
                                  {group.section}
                                </TableCell>
                              </TableRow>
                            ),
                            ...group.rows.map((row, index) => (
                              <TableRow key={`product-row-${group.section}-${index}`}>
                                <TableCell>{row.label}</TableCell>
                                <TableCell align="center">
                                  <Chip
                                    size="small"
                                    label={row.isService ? 'Service' : 'Produit'}
                                    color={row.isService ? 'success' : 'default'}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <Chip size="small" label={row.cycle === 'mensuel' ? 'Mensuel' : row.cycle === 'annuel' ? 'Annuel' : 'One shot'} />
                                </TableCell>
                                <TableCell align="right">{row.quantity.toFixed(2)}</TableCell>
                                <TableCell align="right">{formatCurrency(row.unitPrice)}</TableCell>
                                <TableCell align="right">{formatCurrency(getAnnualizedLineTotal(row))}</TableCell>
                              </TableRow>
                            )),
                            (
                              <TableRow key={`product-subtotal-${group.section}`} sx={{ backgroundColor: '#e6f6fd' }}>
                                <TableCell colSpan={5} align="right" sx={{ fontWeight: 700 }}>
                                  {`Sous-total (${group.subtotalUnitLabel})`}
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                  {formatCurrency(group.subtotalDisplay)}
                                </TableCell>
                              </TableRow>
                            ),
                          ])
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  {(() => {
                    const esetStatus = getEsetStatus(viewing);
                    const esetComparison = getEsetComparison(viewing);
                    if (esetStatus.hidden) {
                      return (
                        <Alert severity="info">
                          Aucune donnée technique ESET disponible.
                        </Alert>
                      );
                    }

                    if (esetStatus.isOverrun) {
                      return (
                        <Stack spacing={1}>
                          <Alert severity="error">Dépassement ESET détecté</Alert>
                          <Typography variant="body2" color="text.secondary">
                            Contrat: {Number(esetComparison?.contract?.quantity || 0).toFixed(2)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Technique totale: {Number(esetComparison?.technical?.quantity || 0).toFixed(2)}
                          </Typography>
                        </Stack>
                      );
                    }

                    return (
                      <Stack spacing={1}>
                        <Alert severity={esetStatus.color === 'success' ? 'success' : 'warning'}>
                          {esetStatus.label}
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                          Contrat: {Number(esetComparison?.contract?.quantity || 0).toFixed(2)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Technique totale: {Number(esetComparison?.technical?.quantity || 0).toFixed(2)}
                        </Typography>
                      </Stack>
                    );
                  })()}
                </Paper>

                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  {(() => {
                    const o365Status = getO365Status(viewing);
                    const o365Comparison = getO365Comparison(viewing);
                    if (o365Status.hidden) {
                      return (
                        <Alert severity="info">
                          Aucune donnée technique O365 disponible.
                        </Alert>
                      );
                    }

                    if (o365Status.isOverrun) {
                      return (
                        <Stack spacing={1}>
                          <Alert severity="error">Dépassement O365 détecté</Alert>
                          <Typography variant="body2" color="text.secondary">
                            Contrat: {Number(o365Comparison?.contract?.quantity || 0).toFixed(2)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Technique totale: {Number(o365Comparison?.technical?.quantity || 0).toFixed(2)}
                          </Typography>
                        </Stack>
                      );
                    }

                    return (
                      <Stack spacing={1}>
                        <Alert severity={o365Status.color === 'success' ? 'success' : 'warning'}>
                          {o365Status.label}
                        </Alert>
                        <Typography variant="body2" color="text.secondary">
                          Contrat: {Number(o365Comparison?.contract?.quantity || 0).toFixed(2)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Technique totale: {Number(o365Comparison?.technical?.quantity || 0).toFixed(2)}
                        </Typography>
                      </Stack>
                    );
                  })()}
                </Paper>
              </Box>

              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Récapitulatif</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Poste</TableCell>
                        <TableCell align="right">Mensuel</TableCell>
                        <TableCell align="right">Annuel</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Services</TableCell>
                        <TableCell align="right">{formatCurrency(getContractMonthlyValue(viewing))}</TableCell>
                        <TableCell align="right">{formatCurrency(getContractEstimatedValue(viewing))}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Produits</TableCell>
                        <TableCell align="right">{formatCurrency(getContractProductsMonthlyValue(viewing))}</TableCell>
                        <TableCell align="right">{formatCurrency(getContractProductsAnnualValue(viewing))}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Total global</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(getContractTotalMonthlyValue(viewing))}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{formatCurrency(getContractTotalAnnualValue(viewing))}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewContractOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewMatrixOpen} onClose={() => setViewMatrixOpen(false)} fullScreen>
        <DialogTitle>
          {`Matrice - ${viewing?.name || 'Sans nom'}`}
        </DialogTitle>
        <DialogContent dividers>
          {viewing ? (
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant={matrixEditMode ? 'contained' : 'outlined'}
                    onClick={() => {
                      if (matrixEditMode) {
                        setMatrixDraft(buildEditableContractMatrix(viewing));
                      }
                      setMatrixEditMode((current) => !current);
                      setMatrixError('');
                    }}
                  >
                    {matrixEditMode ? 'Annuler édition' : 'Modifier matrice'}
                  </Button>
                  {matrixEditMode && (
                    <Button
                      size="small"
                      variant="contained"
                      onClick={saveMatrixChanges}
                      disabled={matrixSaving}
                    >
                      {matrixSaving ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                  )}
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant={matrixViewMode === 'columns' ? 'contained' : 'outlined'}
                    onClick={() => setMatrixViewMode('columns')}
                  >
                    Colonnes
                  </Button>
                  <Button
                    size="small"
                    variant={matrixViewMode === 'compact' ? 'contained' : 'outlined'}
                    onClick={() => setMatrixViewMode('compact')}
                  >
                    Compact
                  </Button>
                </Stack>
              </Stack>
              {matrixError && <Alert severity="error">{matrixError}</Alert>}
              {matrixCrossGroups.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Aucune ligne de matrice.
                  </Typography>
                </Paper>
              ) : matrixViewMode === 'columns' ? (
                <Box
                  sx={{
                    columnCount: { xs: 1, lg: 2, xl: 3 },
                    columnGap: 1.5,
                  }}
                >
                  {matrixCrossGroups.map(([skill, rows]) => (
                    <Accordion
                      key={skill}
                      disableGutters
                      sx={{
                        mb: 1.5,
                        breakInside: 'avoid',
                        display: 'inline-block',
                        width: '100%',
                      }}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {skill} ({rows.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 0 }}>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Niveau</TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="center">Admin</TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="center">Ingé</TableCell>
                                <TableCell sx={{ fontWeight: 700 }} align="center">Pilotage</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {rows.map((row, rowIndex) => (
                                <TableRow key={`${skill}-${row.level}-${rowIndex}`} hover>
                                  <TableCell>{row.level}</TableCell>
                                  {['admin', 'ingenieur', 'pilotage'].map((profileKey) => {
                                    const cell = row.cells?.[profileKey];
                                    if (!cell) {
                                      return (
                                        <TableCell key={profileKey} align="center">
                                          <Typography variant="caption" color="text.disabled">—</Typography>
                                        </TableCell>
                                      );
                                    }

                                    return (
                                      <TableCell key={profileKey} align="center">
                                        {matrixEditMode ? (
                                          <Button
                                            size="small"
                                            variant="contained"
                                            color={cell.included ? 'success' : 'error'}
                                            onClick={() => toggleMatrixIncluded(cell._index)}
                                            sx={{ minWidth: 56, py: 0.25 }}
                                          >
                                            {cell.included ? 'Oui' : 'Non'}
                                          </Button>
                                        ) : (
                                          <Chip
                                            size="small"
                                            label={cell.included ? 'Oui' : 'Non'}
                                            color={cell.included ? 'success' : 'error'}
                                          />
                                        )}
                                      </TableCell>
                                    );
                                  })}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>Compétence 1</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Niveau</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">Admin</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">Ingé</TableCell>
                        <TableCell sx={{ fontWeight: 700 }} align="center">Pilotage</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {matrixFlatRows.map((row, rowIndex) => (
                        <TableRow key={`matrix-flat-${row.skill}-${row.level}-${rowIndex}`} hover>
                          <TableCell>{row.skill}</TableCell>
                          <TableCell>{row.level}</TableCell>
                          {['admin', 'ingenieur', 'pilotage'].map((profileKey) => {
                            const cell = row.cells?.[profileKey];
                            if (!cell) {
                              return (
                                <TableCell key={profileKey} align="center">
                                  <Typography variant="caption" color="text.disabled">—</Typography>
                                </TableCell>
                              );
                            }

                            return (
                              <TableCell key={profileKey} align="center">
                                {matrixEditMode ? (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color={cell.included ? 'success' : 'error'}
                                    onClick={() => toggleMatrixIncluded(cell._index)}
                                    sx={{ minWidth: 56, py: 0.25 }}
                                  >
                                    {cell.included ? 'Oui' : 'Non'}
                                  </Button>
                                ) : (
                                  <Chip
                                    size="small"
                                    label={cell.included ? 'Oui' : 'Non'}
                                    color={cell.included ? 'success' : 'error'}
                                  />
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewMatrixOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
